import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { List, LogOut } from 'lucide-react';
import { SECTIONS, getStepByRoute } from '../demo/steps';
import { clearCapturedDetails, readCapturedDetails } from '../demo/capturedDetails';
import { hasAccess, revokeAccess } from '../demo/signupFlag';
import { signOutVisitor } from '../pages/start/services/signupService';
import ContentsDrawer from './ContentsDrawer';

const LOGO_SRC = `${process.env.PUBLIC_URL}/Niroggyan_logo.svg`;

export default function TopNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const step = getStepByRoute(pathname);
  // Who signed up on this browser (sessionStorage; may be empty on a later visit).
  const details = readCapturedDetails();
  const signedIn = hasAccess();
  const who = details?.name || details?.organisation || '';
  const initial = (who.trim()[0] || 'N').toUpperCase();
  const [contentsOpen, setContentsOpen] = useState(false);
  const [visitorOpen, setVisitorOpen] = useState(false);
  const contentsBtnRef = useRef(null);
  const visitorRef = useRef(null);

  const closeContents = useCallback(() => {
    setContentsOpen(false);
    contentsBtnRef.current?.focus();
  }, []);

  // Navigating anywhere (including browser back/forward) closes the overlays.
  useEffect(() => {
    setContentsOpen(false);
    setVisitorOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!visitorOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setVisitorOpen(false);
    };
    const onPointerDown = (e) => {
      if (!visitorRef.current?.contains(e.target)) setVisitorOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [visitorOpen]);

  // Log out: forget the sign-up on this browser and end the Firebase session,
  // then back to Home; every page after it asks for the form again (App.js).
  const logOut = () => {
    revokeAccess();
    clearCapturedDetails();
    signOutVisitor();
    setVisitorOpen(false);
    navigate('/', { replace: true });
  };

  // The drawer sits outside <header>: the header's backdrop-filter would
  // otherwise become the containing block of the drawer's fixed position.
  return (
    <>
      <header className="topnav">
        <Link to="/" className="topnav__logo" data-trapezium-id="shell.home-logo" aria-label="NirogGyan home">
          <img src={LOGO_SRC} alt="" width="160" height="27" />
        </Link>

        <nav className="topnav__sections" aria-label="Sections">
          <ul>
            {SECTIONS.map((section) => {
              const active = step?.section === section.key;
              let ariaCurrent;
              if (active) ariaCurrent = pathname === section.route ? 'page' : 'true';
              return (
                <li key={section.key}>
                  <Link
                    to={section.route}
                    className={active ? 'topnav__link is-active' : 'topnav__link'}
                    aria-current={ariaCurrent}
                    data-trapezium-id={`shell.nav-${section.key}`}
                  >
                    {section.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="topnav__actions">
          <div className="visitor" ref={visitorRef}>
            <button
              type="button"
              className="visitor__avatar"
              aria-label={`${initial}, ${who || 'demo visitor'}: account menu`} /* starts with the visible initial (WCAG 2.5.3) */
              aria-expanded={visitorOpen}
              aria-controls={visitorOpen ? 'visitor-popover' : undefined}
              data-trapezium-id="shell.open-visitor"
              onClick={() => setVisitorOpen((o) => !o)}
            >
              {initial}
            </button>
            {visitorOpen && (
              <div id="visitor-popover" className="visitor__popover" data-trapezium-state="open">
                <div className="visitor__who" data-trapezium-private="true">
                  <strong>{who || 'Demo visitor'}</strong>
                  {details?.name && details.organisation ? <span>{details.organisation}</span> : null}
                  {details?.email ? <span>{details.email}</span> : null}
                </div>
                {signedIn ? (
                  <button type="button" className="visitor__logout" onClick={logOut} data-trapezium-id="shell.logout">
                    <LogOut size={16} aria-hidden="true" />
                    Log out
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <button
            type="button"
            ref={contentsBtnRef}
            className="topnav__contents"
            aria-expanded={contentsOpen}
            aria-controls={contentsOpen ? 'contents-drawer' : undefined}
            aria-haspopup="dialog"
            data-trapezium-id="shell.open-contents"
            onClick={() => setContentsOpen((o) => !o)}
          >
            <List aria-hidden="true" size={20} />
            <span>Contents</span>
          </button>
        </div>
      </header>
      {contentsOpen && (
        <ContentsDrawer currentStep={step} onClose={closeContents} toggleRef={contentsBtnRef} />
      )}
    </>
  );
}
